<template>
  <div v-if="open" id="security-info-modal" class="modal absolute-modal" role="dialog" aria-modal="true">
    <div class="modal-overlay" aria-hidden="true" :class="isMobileFullscreen ? 'hidden' : ''"></div>
    <div class="inner rounded-lg bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 relative max-w-3xl w-full" :class="isMobileFullscreen ? 'absolute inset-0 rounded-none border-0 shadow-none flex flex-col' : ''" :style="isMobileFullscreen ? mobileInnerStyle : { '-webkit-transform': 'translate3d(0, 0, 0)', maxHeight: '90vh' }">
      <!-- Scrollable container -->
      <div class="flex flex-col scrollable-content" :class="isMobileFullscreen ? 'flex-1' : ''" :style="isMobileFullscreen ? null : { maxHeight: 'calc(90vh - 8px)' }">
        <!-- Topbar -->
        <div
          class="flex flex-col items-start self-stretch bg-white dark:bg-slate-800"
          :style="{
            padding: '24px 24px 32px 24px',
            gap: '8px'
          }"
        >
          <div class="flex items-center justify-between self-stretch">
            <h2 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">How we keep your data safe</h2>
            <a href="#" class="btn-icon btn-transparent w-10" role="button" @click.prevent="$emit('close')">
              <Icon icon="close" />
            </a>
          </div>
          <div class="flex items-center gap-2 text-orange">
            <Icon icon="security" class="w-5 h-5" />
            <span class="text-sm font-medium leading-5">Data security</span>
          </div>
          <h3 class="text-slate-800 dark:text-slate-100 text-2xl font-semibold leading-8 mt-2 grow basis-0 shrink-0">We take your data and security seriously</h3>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-2">Data security is as important to us as it is to you.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">Candidly, when transmitting and storing data over the Internet, there is no way to guarantee 100% security of that information.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">However, we take as many measures as possible in order to protect your information from unauthorized access as best as possible using the most up to date security measures and best practices.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">Here's how we're working to keep your data safe:</p>
        </div>

        <!-- Content -->
        <div class="flex flex-col items-start self-stretch" :style="{ padding: '0 24px 96px 24px' }">
          <h4 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mt-6 grow basis-0 shrink-0">We've Partnered with MX Technologies</h4>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-2">We have partnered with MX Technologies, a leading Saas company in the financial technology space, to allow you the option to securely connect to supported financial institutions.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">MX works with over 2,000 banks, credit unions, fintechs and more to offer secure connections to financial institutions and data.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">Through this partnership with MX, should you wish to register connections with supported institutions, you will be required to enter your online banking credentials for that institution.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">Do note that your credentials never touch My10x servers and are not stored by us in any manner. Instead, MX processes them directly with the respective institution, and we receive a fully encrypted authentication token, which allows us to connect to your account and data in order to import it into My10x. <strong>This token can be revoked at any time by you</strong> in the Manage Connections section of My Wallet in My10.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">At no time do we have the ability to directly access your account in order to make changes to it.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">As a reminder, we do fully support manual entry of all of this data, which doesn’t require you to share online banking credentials.</p>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-4">For more information and specifics on MX’s security processes and how they protect your data, <a href="https://hubspot.mx.com/hubfs/security-whitepaper.pdf" class="text-orange text-sm font-medium leading-5 hover:underline" target="_blank" rel="noopener noreferrer">view this whitepaper</a>.</p>

          <h4 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mt-8 grow basis-0 shrink-0">We Protect Your Data</h4>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5 mt-2">We have spent considerable time and resources in building My10x with the most up to date security measures and best practices in order to best protect your data. Some, but not all, of these practices are outlined below:</p>

          <h5 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 mt-4">Encrypted Data</h5>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">We encrypt all data transmissions using public key cryptography algorithms such as AES-256 and AES-128, with a minimum key size of 128 bit encryption. This is the same level of encryption that most banks use when storing and processing your data.</p>

          <h5 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 mt-4">SSL/TLS</h5>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">My10x utilizes SSL, or Secure Sockets Layer, which is an encryption-based Internet security protocol. You may be familiar with this as you’ll see “https” in the page url and the lock icon in the browser, indicating that SSL/TLS is implemented on that website or application. SSL/TLS is used to prevent unauthorized third parties from intercepting data transmissions.</p>

          <h5 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 mt-4">Two-Factor Authentication</h5>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">My10x offers Two-Factor Authentication (2FA) as an additional security option for users. While not impenetrable, with 2FA enabled, every time you login, we'll send you a random 6-digit code via SMS to help verify your account authentication. This helps to prevent unwanted access to your account.</p>

          <h5 class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6 mt-4">Automatic Logout After Inactivity</h5>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">My10x will also anonymously monitor your activity level when using the application, and will log you out after a set period of inactivity. This action helps to ensure your account security if you forget to logout from My10x.</p>

          <h4 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7 mt-8 grow basis-0 shrink-0">Questions / Concerns?</h4>
          <p class="text-slate-800 dark:text-slate-300 text-sm font-normal leading-5">If you have any questions or concerns, please get in touch with us:</p>
          <p class="text-orange text-sm font-medium leading-5">my10x@10xtravel.com</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/ui/icon/icon.vue';

export default {
  name: 'SecurityInfoModal',
  components: { Icon },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      isSmallScreen: false,
    };
  },
  computed: {
    isMobileFullscreen() {
      return this.isSmallScreen;
    },
    mobileInnerStyle() {
      return {
        '-webkit-transform': 'translate3d(0, 0, 0)',
        transform: 'translate3d(0, 0, 0)',
        width: '100vw',
        height: '100dvh',
        minHeight: '100svh',
        maxHeight: 'none',
      };
    },
  },
  mounted() {
    this.updateScreen();
    window.addEventListener('resize', this.updateScreen);
    window.addEventListener('orientationchange', this.updateScreen);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScreen);
    window.removeEventListener('orientationchange', this.updateScreen);
  },
  methods: {
    updateScreen() {
      try {
        const mq = window.matchMedia('(max-width: 767.98px)');
        this.isSmallScreen = !!mq.matches;
      } catch (e) {
        this.isSmallScreen = window.innerWidth < 768;
      }
    },
  },
};
</script>

<style scoped>
/* Scrollable content with auto-hide scrollbar */
.scrollable-content {
  overflow-y: auto;
  overflow-x: hidden;
}

/* Firefox - auto-hide scrollbar */
.scrollable-content {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.3s ease;
}

.scrollable-content:hover,
.scrollable-content:active {
  scrollbar-color: rgba(100, 116, 139, 0.5) rgba(241, 245, 249, 0.3);
}

/* Chrome, Safari and Opera - auto-hide scrollbar */
.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background-color: transparent;
  transition: background-color 0.3s ease;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 3px;
  transition: background-color 0.3s ease;
}

.scrollable-content:hover::-webkit-scrollbar-track {
  background-color: rgba(241, 245, 249, 0.3);
}

.scrollable-content:hover::-webkit-scrollbar-thumb {
  background-color: rgba(100, 116, 139, 0.5);
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(100, 116, 139, 0.7);
}
</style>
